import React, { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";

/**
 * Phase 8A — Admin audit log viewer.
 *
 * Paginated table of everything captured by AdminAuditMiddleware plus
 * targeted rows written from routers (autonomy actions) or bypass
 * shortcuts (hourly cap / plan limit / rate limit / module gate).
 */

const OUTCOMES = ["", "success", "denied", "error"];

function outcomeColor(o) {
    if (o === "success") return "text-[var(--color-success)] bg-[var(--color-success)]/10";
    if (o === "denied") return "text-neutral-400 bg-neutral-400/10";
    if (o === "error") return "text-[var(--color-alert)] bg-[var(--color-alert)]/10";
    return "text-text-secondary bg-white/5";
}

export default function AdminAuditLog() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(100);
    const [filters, setFilters] = useState({ action: "", target_type: "", outcome: "" });
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams();
            q.set("limit", String(limit));
            q.set("offset", String(offset));
            if (filters.action) q.set("action", filters.action);
            if (filters.target_type) q.set("target_type", filters.target_type);
            if (filters.outcome) q.set("outcome", filters.outcome);
            const res = await apiFetch(`/admin/audit-log?${q}`);
            setRows(res.items || []);
            setTotal(res.total || 0);
        } catch (e) {
            toast.error(e.message || "Failed to load audit log");
        } finally { setLoading(false); }
    }, [offset, limit, filters]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = () => {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        window.location.href = `${base}/api/admin/audit-log/export.csv`;
    };

    // Phase 22b · dedicated community audit-log CSV export (private/corp access control lineage)
    const exportCommunityCsv = () => {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        window.location.href = `${base}/api/admin/community/audit-log/export.csv`;
    };

    if (!isAdminRole(user?.role)) {
        return (
            <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-semibold">Admin only</h1>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="flex-1 p-6 md:p-8 max-w-7xl mx-auto w-full" data-testid="admin-audit-log">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                                Audit trail
                            </div>
                            <h1 className="text-3xl md:text-4xl font-semibold">Admin actions · {total.toLocaleString()}</h1>
                            <p className="text-sm text-text-secondary mt-1">
                                Every write to <code>/api/admin/*</code> plus every technical bypass. Retention 365 days.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={load}
                                data-testid="audit-refresh"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5 flex items-center gap-1"
                            >
                                <RefreshCw className="h-3 w-3" /> Refresh
                            </button>
                            <button
                                onClick={exportCsv}
                                data-testid="audit-export-csv"
                                className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-1.5 flex items-center gap-1"
                            >
                                <Download className="h-3 w-3" /> Export CSV
                            </button>
                            <button
                                onClick={exportCommunityCsv}
                                data-testid="audit-export-community-csv"
                                title="Community audit log (Phase 22a/b): org/private access control events"
                                className="text-xs rounded-full border border-[var(--color-secondary)]/50 text-[var(--color-secondary)] hover:bg-[var(--color-secondary)]/10 px-4 py-1.5 flex items-center gap-1"
                            >
                                <Download className="h-3 w-3" /> Community CSV
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="grid gap-3 md:grid-cols-4 mb-6">
                        <input
                            data-testid="audit-filter-action"
                            placeholder="Action contains…"
                            value={filters.action}
                            onChange={(e) => { setOffset(0); setFilters((s) => ({ ...s, action: e.target.value })); }}
                            className="rounded-lg bg-[var(--color-card)] border border-[var(--color-border)] px-3 py-2 text-sm text-text-main focus:border-[var(--color-primary)] outline-none"
                        />
                        <input
                            data-testid="audit-filter-target-type"
                            placeholder="Target type…"
                            value={filters.target_type}
                            onChange={(e) => { setOffset(0); setFilters((s) => ({ ...s, target_type: e.target.value })); }}
                            className="rounded-lg bg-[var(--color-card)] border border-[var(--color-border)] px-3 py-2 text-sm text-text-main focus:border-[var(--color-primary)] outline-none"
                        />
                        <select
                            data-testid="audit-filter-outcome"
                            value={filters.outcome}
                            onChange={(e) => { setOffset(0); setFilters((s) => ({ ...s, outcome: e.target.value })); }}
                            className="rounded-lg bg-[var(--color-card)] border border-[var(--color-border)] px-3 py-2 text-sm text-text-main focus:border-[var(--color-primary)] outline-none"
                        >
                            {OUTCOMES.map((o) => (
                                <option key={o || "any"} value={o}>{o ? `Outcome: ${o}` : "Any outcome"}</option>
                            ))}
                        </select>
                        <div className="text-xs text-text-secondary flex items-center">
                            Showing {offset + 1}-{Math.min(offset + rows.length, total)} of {total}
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead className="text-[10px] uppercase tracking-widest text-text-secondary bg-[var(--color-panel-ai)]">
                                        <tr>
                                            <th className="text-left py-2 px-3">Timestamp</th>
                                            <th className="text-left py-2 px-3">Admin</th>
                                            <th className="text-left py-2 px-3">Action</th>
                                            <th className="text-left py-2 px-3">Target</th>
                                            <th className="text-left py-2 px-3">Outcome</th>
                                            <th className="text-left py-2 px-3">Bypass</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <tr key={r.id} className="border-t border-[var(--color-border)]/60" data-testid={`audit-row-${r.id}`}>
                                                <td className="py-2 px-3 whitespace-nowrap text-text-secondary tabular-nums">
                                                    {r.timestamp?.slice(0, 19).replace("T", " ")}
                                                </td>
                                                <td className="py-2 px-3 text-text-main truncate max-w-[180px]">{r.admin_email}</td>
                                                <td className="py-2 px-3 text-text-main truncate max-w-xs">{r.action}</td>
                                                <td className="py-2 px-3 text-text-secondary truncate max-w-[200px]">
                                                    {r.target_type && (
                                                        <span>{r.target_type}{r.target_id ? ` · ${String(r.target_id).slice(0, 8)}` : ""}</span>
                                                    )}
                                                </td>
                                                <td className="py-2 px-3">
                                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${outcomeColor(r.outcome)}`}>
                                                        {r.outcome}{r.status_code ? ` · ${r.status_code}` : ""}
                                                    </span>
                                                </td>
                                                <td className="py-2 px-3 text-text-secondary">
                                                    {r.bypass_kind ? (
                                                        <span className="text-[10px] px-2 py-0.5 rounded bg-neutral-400/10 text-neutral-400">
                                                            {r.bypass_kind}
                                                        </span>
                                                    ) : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                        {rows.length === 0 && (
                                            <tr><td colSpan={6} className="py-8 text-center text-text-secondary">No rows match your filters.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="mt-4 flex items-center justify-end gap-2">
                        <button
                            disabled={offset === 0}
                            onClick={() => setOffset(Math.max(0, offset - limit))}
                            data-testid="audit-prev"
                            className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary px-4 py-1.5 disabled:opacity-40"
                        >
                            ← Prev
                        </button>
                        <button
                            disabled={offset + limit >= total}
                            onClick={() => setOffset(offset + limit)}
                            data-testid="audit-next"
                            className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary px-4 py-1.5 disabled:opacity-40"
                        >
                            Next →
                        </button>
                    </div>
                </main>
            </div>
        </div>
    );
}
